import { useRef, useCallback, useState, useMemo } from 'preact/hooks';
import { useMapData } from '../hooks/useMapData';
import { useDisplayMode } from '../hooks/useDisplayMode';
import { useFilters, type FilterConfig } from '../hooks/useFilters';

function getFilterConfigs( mapType: string ): FilterConfig[] {
	return [
		{
			key: 'locationType',
			field: 'location_type',
			label: mapType === 'pif-map' ? 'Filter by Type' : 'Filter by Location Type',
			placeholder: 'All Types',
			id: 'str-maps-filter-type',
		},
		{
			key: 'state',
			field: 'state',
			label: 'By State',
			placeholder: 'All States',
			id: 'str-maps-filter-state',
		},
	];
}

// Components.
import Map, { type MapHandle } from './map/Map';
import { ZoomControls } from './map/ZoomControls';
import LocationDialog from './map/LocationDialog';
import ListView from './ListView';
import ViewToggle from './ViewToggle';
import FilterBar from './FilterBar';

/**
 * MapApp component.
 * Top-level orchestrator that fetches location data and renders the map or list view.
 *
 * @param {Object} props         - Component props.
 * @param {string} props.mapType - The map type to fetch data for.
 * @return {*} jsx
 */
export default function MapApp( { mapType }: { mapType: string } ) {
	// Fetch map location data from the REST API.
	const { locations, isLoading, error } = useMapData( mapType );

	// Display mode: map or list. Mobile defaults to list.
	const { displayMode, setDisplayMode, isMobile } = useDisplayMode();

	// Filter configs — label varies by map type.
	const filterConfigs = useMemo( () => getFilterConfigs( mapType ), [ mapType ] );

	// Filter state.
	const filters = useFilters( locations, filterConfigs );

	// Extract just the MapLocation objects for the Map component (doesn't need originalIndex).
	const filteredMapLocations = useMemo(
		() => filters.filteredLocations.map( ( { location } ) => location ),
		[ filters.filteredLocations ]
	);

	// Ref to access Map zoom methods.
	const mapRef = useRef< MapHandle >( null );

	// Shared dialog state — used by both map and list views.
	const [ selectedIndex, setSelectedIndex ] = useState< number | null >( null );
	const selectedLocation = selectedIndex !== null ? locations[ selectedIndex ] ?? null : null;

	// Callback for opening the dialog from list view (receives original index).
	const handleLocationClick = useCallback( ( index: number ) => setSelectedIndex( index ), [] );
	const handleDialogClose = useCallback( () => setSelectedIndex( null ), [] );

	/**
	 * Handle location click from the Map component.
	 * The Map receives the filtered subset, so its internal index refers to filteredMapLocations.
	 * We need to convert that to the original locations index for the dialog.
	 */
	const handleMapLocationClick = useCallback(
		( filteredIndex: number ) => {
			const originalIndex = filters.filteredLocations[ filteredIndex ]?.originalIndex;
			if ( originalIndex !== undefined ) {
				setSelectedIndex( originalIndex );
			}
		},
		[ filters.filteredLocations ]
	);

	// Zoom callback for ZoomControls — delegates to Map via ref.
	const zoomCallback = useCallback( ( type: 'zoomIn' | 'zoomOut' | 'zoomReset' ) => {
		if ( ! mapRef.current ) {
			return;
		}

		switch ( type ) {
			case 'zoomIn':
				mapRef.current.zoomIn();
				break;
			case 'zoomOut':
				mapRef.current.zoomOut();
				break;
			case 'zoomReset':
				mapRef.current.zoomReset();
				break;
		}
	}, [] );

	/**
	 * Clear all filters and move focus to the first combobox trigger.
	 * Used by the no-results message (whose button unmounts after clearing).
	 */
	const handleNoResultsClear = useCallback( () => {
		filters.clearAllFilters();
		requestAnimationFrame( () => {
			document.querySelector< HTMLButtonElement >( '#str-maps-filter-type' )?.focus();
		} );
	}, [ filters.clearAllFilters ] );

	// Switch to list view via the skip link, then move focus to the list container.
	const handleSkipToList = useCallback( ( e: Event ) => {
		e.preventDefault();
		setDisplayMode( 'list' );

		// After Preact renders the list view, move focus to the list container.
		requestAnimationFrame( () => {
			document.getElementById( 'str-maps-list' )?.focus();
		} );
	}, [ setDisplayMode ] );

	if ( error ) {
		return <p className="str-maps__error" role="alert">{ error }</p>;
	}

	if ( isLoading ) {
		return <div className="str-maps__loader" role="status" aria-label="Loading locations" />;
	}

	return (
		<>
			<FilterBar
				{ ...filters }
				locations={ locations }
				onSearchSelect={ handleLocationClick }
				mapType={ mapType }
			/>

			{ /* Skip link: lets keyboard/AT users bypass the map and switch to list view. */ }
			{ ! isMobile && displayMode === 'map' && (
				<a
					href="#str-maps-list"
					className="str-maps__skip-link screen-reader-text"
					onClick={ handleSkipToList }
				>
					Skip map, switch to list view
				</a>
			) }

			{ filters.hasActiveFilters && filters.filteredLocations.length === 0 ? (
				<p className="str-maps__no-results" role="status">
					No locations match your current filters.
					<button
						type="button"
						className="str-maps__no-results-clear"
						onClick={ handleNoResultsClear }
					>
						Clear all filters
					</button>
				</p>
			) : (
				<>
					{ displayMode === 'map' && (
						<Map
							ref={ mapRef }
							locations={ filteredMapLocations }
							onLocationClick={ handleMapLocationClick }
							selectedIndex={ selectedIndex }
							filteredLocations={ filters.filteredLocations }
						/>
					) }

					{ displayMode === 'list' && (
						<ListView
							locations={ filters.filteredLocations }
							onCardClick={ handleLocationClick }
							selectedIndex={ selectedIndex }
						/>
					) }
				</>
			) }

			{ ! isMobile && (
				<div className={ `str-maps__controls ${ displayMode === 'list' ? 'str-maps__controls--list' : '' }` }>
					<ViewToggle mode={ displayMode } setMode={ setDisplayMode } />
					{ displayMode === 'map' && (
						<ZoomControls zoomCallback={ zoomCallback } />
					) }
				</div>
			) }

			{ /* Live region for screen reader announcements. */ }
			<div className="screen-reader-text" aria-live="polite" aria-atomic="true">
				{ displayMode === 'map'
					? `Map view active, showing ${ filteredMapLocations.length } locations`
					: `List view active, showing ${ filters.filteredLocations.length } locations` }
			</div>

			<LocationDialog
				location={ selectedLocation }
				open={ selectedIndex !== null }
				onClose={ handleDialogClose }
				mapType={ mapType }
			/>
		</>
	);
}
